import React from 'react';
import get from 'lodash/get';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessage, intlShape, injectIntl } from 'react-intl';
import Head from 'next/head';
import { Router } from '../router';
import { isFetchingSelector, errorSelector, indexSeoSelector } from './selectors';
import { searchParametersSelector, autoCompleteSelector } from '../Serp/selectors';
import { locationChange } from '../tracking/actions';
import {
  setSearchParams,
  fetchAutocompleteRequest,
  activateUserSearchInteraction,
} from '../Serp/actions';
import {
  transformSearchParametersToQueryObject,
  clearSearchParameters,
  getWhatAccentedByVertical,
} from '../utils/searchParameters';
import Layout from '../common/Layout';
import Logo from '../common/Logo';
import GdprCookieBanner from '../Gdpr/components/mobile/GdprCookieBanner/container';
import messages from './messages';
import { configSelector } from '~/common/selectors/config';

const { searchSlogan, homepageMetaTitle, metaTitle, metaDescription } = messages;

export default Component => {
  class Index extends React.Component {
    constructor(props) {
      super(props);
      this.onSearchSubmit = this.onSearchSubmit.bind(this);
      this.onSearchChanged = this.onSearchChanged.bind(this);
      this.updateQueryUrl = this.updateQueryUrl.bind(this);
    }

    componentDidMount() {
      Router.prefetch('/serp');
    }

    onSearchSubmit(formFilters) {
      const { config } = this.props;
      const verticalName = config.verticalName;
      const newSearchParameters = {
        ...clearSearchParameters(this.props.searchParameters),
        what_d: formFilters.what_d,
        whatAccented: getWhatAccentedByVertical(verticalName, {
          what: formFilters.what_d,
          where: formFilters.where,
        }),
        where: formFilters.where,
        routeName: 'serp_search',
        pageName: 'serp',
        isSeoUrl: false,
        type_id: formFilters.type_id,
        suggester: formFilters.suggester,
      };

      this.props.setUserSearchInteraction();
      this.props.setSearchParams(newSearchParameters, true);
      this.updateQueryUrl(verticalName, newSearchParameters);
    }

    onSearchChanged(search) {
      this.props.fetchAutocompleteRequest(search);
    }

    updateQueryUrl = (verticalName, searchParameters) => {
      const query = transformSearchParametersToQueryObject(searchParameters, verticalName);

      Router.pushRoute('serp_search', query).then(() => window.scrollTo(0, 0));
    };

    render() {
      const { intl, config, searchParameters, autoCompleteItems } = this.props;
      const verticalName = config.verticalName;
      const hasAutoCompleteFieldsInHomePageSearch = get(config, [
        'vertical',
        'allowAutoCompleteFieldsIn',
        'homePageSearch',
      ]);
      const isSemUrl = get(searchParameters, 'isSemUrl');

      const title = intl.formatMessage(metaTitle);
      const description = intl.formatMessage(metaDescription);

      return (
        <Layout shouldShowSocialMedia={!isSemUrl}>
          <Head>
            <title>{title}</title>
            <meta name="description" content={description} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta name="robots" content="index,follow" />
          </Head>
          <div className="main">
            <GdprCookieBanner />
            <div className="header">
              <Logo type={verticalName} width={132} />
              <h1 data-test="header-home__title">
                <FormattedMessage {...homepageMetaTitle} />
              </h1>
              <h2 data-test="header-home__subtitle">
                <FormattedMessage {...searchSlogan} />
              </h2>
            </div>
            <Component
              onSearchSubmit={this.onSearchSubmit}
              onSearchChanged={this.onSearchChanged}
              hasAutoCompleteFieldsInHomePageSearch={hasAutoCompleteFieldsInHomePageSearch}
              autoCompleteItems={autoCompleteItems}
              {...this.props}
            />
          </div>
          <style jsx>
            {`
              .header {
                padding: 55px 25px 25px;
                text-align: center;
              }

              h1 {
                font-size: 16px;
                line-height: 1.25;
                margin: 15px 25px 5px;
              }

              h2 {
                font-size: 14px;
                opacity: 0.5;
                line-height: 1.25;
                margin: 0 25px;
              }
            `}
          </style>
        </Layout>
      );
    }
  }

  Index.defaultProps = {
    autoCompleteItems: [],
  };

  Index.propTypes = {
    setSearchParams: PropTypes.func.isRequired,
    searchParameters: PropTypes.object.isRequired,
    fetchAutocompleteRequest: PropTypes.func.isRequired,
    config: PropTypes.object.isRequired,
    intl: intlShape.isRequired,
    autoCompleteItems: PropTypes.array,
    setUserSearchInteraction: PropTypes.func.isRequired,
  };

  const mapStateToProps = state => ({
    searchParameters: searchParametersSelector(state),
    isFetching: isFetchingSelector(state),
    indexSeo: indexSeoSelector(state),
    error: errorSelector(state),
    config: configSelector(state),
    autoCompleteItems: autoCompleteSelector(state).reduce(
      (acc, current) =>
        acc.concat({
          label: current.text || current.value,
          value: current.value,
          suggester: current.suggester,
        }),
      []
    ),
  });

  const mapDispatchToProps = dispatch => ({
    setSearchParams: (filters, hasTermChanged) =>
      dispatch(setSearchParams(filters, hasTermChanged)),
    setUserSearchInteraction: () => dispatch(activateUserSearchInteraction()),
    locationChange: data => dispatch(locationChange(data)),
    fetchAutocompleteRequest: data => dispatch(fetchAutocompleteRequest({ search: data })),
  });

  return connect(
    mapStateToProps,
    mapDispatchToProps
  )(injectIntl(Index));
};



// WEBPACK FOOTER //
// ./app/Index/index.hoc.js